/**
 * TBB Auction Dashboard — Tab Nav Active State
 *
 * Marks the correct pill tab as active based on the current URL path.
 * Runs once on DOMContentLoaded, no dependencies.
 *
 * Loaded only on /apps/auction-plus/* pages via the auction-proxy
 * custom liquid snippet.
 */
(function () {
  'use strict';

  const TABS = {
    '/apps/auction-plus/purchases': 'won',
    '/apps/auction-plus/watchlists': 'wishlist',
    '/apps/auction-plus/offers': 'offers'
  };

  // Tag every Auction Plus button with `.tbb-btn-condensed` so they pick
  // up the design-system condensed height (44px). Done via JS rather than
  // CSS overrides because the app injects buttons with different border
  // thicknesses on primary vs secondary, and overriding `.btn--primary`
  // / `.btn--secondary` directly produced asymmetric rendered heights.
  // The dedicated class wins via specificity + !important on a single
  // selector.
  function tagCondensedButtons() {
    const proxy = document.querySelector('.auction-proxy');
    if (!proxy) return;
    const buttons = proxy.querySelectorAll(
      '.btn--primary, .button--primary, .btn--secondary, .button--secondary'
    );
    buttons.forEach(function (btn) {
      btn.classList.add('tbb-btn-condensed');
    });
  }

  /**
   * Wishlist "Details" → secondary. AP marks it btn--primary, but it is a
   * row-level link to the product page, not the page's forward action — four
   * identical primary CTAs per screen bury any real hierarchy. Rather than
   * fight the primary CSS blocks (three of them, each with at-source :not()
   * exclusions keyed to AP's class contract), swap AP's own primary classes
   * for its secondary ones. Every existing primary/secondary rule — and any
   * future one that honours the contract — then does the right thing.
   * Class-only change on an <a href>: no nodes move, no handlers touched.
   * Scope verified live 2026-07-16: inside customer-watchlist, the only
   * a.auction-button with an /products/ href is the Details link.
   */
  function demoteWishlistDetails() {
    var links = document.querySelectorAll(
      '.auction-proxy customer-watchlist a.auction-button[href*="/products/"]'
    );
    links.forEach(function (a) {
      a.classList.remove('btn--primary', 'button-primary', 'button--primary');
      a.classList.add('btn--secondary', 'button--secondary');
    });
  }

  /**
   * 2ND CHANCES panel under My Auctions — LeMarc 2026-07-16 (v3): a
   * placeholder-style panel (equal weight to the empty-auctions panel)
   * that navigates to the offers view. Reads like a tab switch: vinyl
   * loader on click (initPageLoader), MY AUCTIONS stays active on /offers
   * (activateTabs). NOTE: live-offer detection (badge/glow) was attempted
   * and REMOVED — AP embeds offer data unreachably from other routes (no
   * JSON endpoint, no server-rendered rows, cross-route <customer-offers>
   * hydrates empty). Needs AP API docs; see memory/handoff before retrying.
   */
  function injectSecondChancesSection() {
    if (window.location.pathname.indexOf('/apps/auction-plus/purchases') !== 0) return;
    var shell = document.querySelector('.auction-proxy');
    if (!shell || shell.querySelector('.tbb-2nd-chances-section')) return;
    var a = document.createElement('a');
    a.className = 'tbb-2nd-chances-section';
    a.href = '/apps/auction-plus/offers';
    var title = document.createElement('span');
    title.className = 'tbb-2nd-title';
    title.textContent = '2nd Chances';
    var cta = document.createElement('span');
    cta.className = 'tbb-2nd-cta';
    cta.textContent = 'Review';
    a.appendChild(title);
    a.appendChild(cta);
    shell.appendChild(a);
  }

  /**
   * On Auction Plus pages, inject a "My Space" tab into the nav so all
   * three buyer surfaces (Auctions / My Wishlist / My Space) share the
   * same horizontal nav. My Space pages already render this tab server-side
   * via the worker's renderEditorLiquid(); this function only injects on
   * Auction Plus pages.
   */
  function injectMySpaceTab(nav) {
    const list = nav.querySelector('ul, ol');
    if (!list) return;
    // Skip if a My Space tab already exists.
    if (list.querySelector('a[href*="/apps/tbb-collector"]')) return;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = '/apps/tbb-collector/edit';
    a.textContent = 'My Space';
    li.appendChild(a);
    list.appendChild(li);
  }

  function activateTabs() {
    var path = window.location.pathname.replace(/\/$/, '');
    // 2nd Chances lives under My Auctions: on /offers, light up MY AUCTIONS
    // so the move there reads as staying in the same place.
    if (path.indexOf('/apps/auction-plus/offers') === 0) {
      path = '/apps/auction-plus/purchases';
    }
    const proxy = document.querySelector('.auction-proxy');
    if (!proxy) return;

    const nav = proxy.querySelector('nav.pb-6');
    if (!nav) return;

    injectMySpaceTab(nav);

    const links = nav.querySelectorAll('a');
    links.forEach(function (link) {
      link.classList.remove('active');
      link.removeAttribute('aria-current');

      var href = link.getAttribute('href') || '';
      // Normalize: strip trailing slash, compare path
      var linkPath = href.replace(/\/$/, '');
      // Handle both absolute and relative hrefs
      if (linkPath.startsWith('http')) {
        try { linkPath = new URL(linkPath).pathname.replace(/\/$/, ''); }
        catch (e) { /* keep as-is */ }
      }

      if (linkPath === path) {
        link.classList.add('active');
        link.setAttribute('aria-current', 'page');
      }
    });
  }

  /**
   * Show the vinyl-loader overlay when a nav tab is clicked. Browser
   * navigates as normal; the overlay covers the brief blank period
   * between page renders so the transition reads as a branded loading
   * state instead of a flash.
   *
   * Loader element is pre-rendered in theme.liquid (hidden by default)
   * via the `tbb-vinyl-loader` snippet — this function only toggles
   * visibility on click.
   */
  function initPageLoader() {
    var loader = document.querySelector('[data-tbb-page-loader]');
    if (!loader || loader.dataset.tbbLoaderInit) return;
    loader.dataset.tbbLoaderInit = '1';

    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('.auction-proxy nav.pb-6 a, a.tbb-2nd-chances-section');
      if (!link) return;
      if (e.defaultPrevented) return;
      // Allow modifier-clicks (open in new tab/window) to bypass.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (link.target === '_blank') return;
      if (!link.href || link.href === window.location.href) return;

      loader.classList.add('is-visible');
    });

    // Hide loader if user navigates back via browser button (page may
    // be restored from bfcache with the visible-class still on).
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        loader.classList.remove('is-visible');
      }
    });
  }

  function init() {
    activateTabs();
    tagCondensedButtons();
    demoteWishlistDetails();
    injectSecondChancesSection();
    initPageLoader();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also observe for late-loaded app proxy content
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) {
        var proxy = document.querySelector('.auction-proxy nav.pb-6');
        if (proxy) {
          init();
          observer.disconnect();
          break;
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Auto-disconnect after 10s to avoid leaks
  setTimeout(function () { observer.disconnect(); }, 10000);
})();

/* ═══════════════════════════════════════════════════════════════════════════
   Strip the auction UID from the win headline — 2026-07-15

   AuctionPlus renders the won-auction header as:

     <div class="text-lg font-medium text-gray-900 md:flex-shrink-0">
       You won the auction! 🥳
       <span class="mx-1 text-gray-400" aria-hidden="true">·</span>
       #sbxzjb78dh1t
     </div>

   The headline itself is ours (set in AP's translations). The `· #uid` is
   appended by AP and cannot be removed there. It also cannot be removed with
   CSS: the UID is a BARE TEXT NODE sharing a parent with the headline, and CSS
   selects elements, not fragments of text. So we remove the text node directly.

   Deliberately narrow: only strips a trailing text node that looks like a UID
   (#hex-ish), and only when it sits next to the `·` separator span. If AP ever
   changes the markup this quietly does nothing rather than eating real copy.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  var UID_RE = /^\s*#?[a-z0-9]{8,}\s*$/i;

  function stripUid(root) {
    var heads = (root || document).querySelectorAll('.auction-proxy .text-lg.font-medium');
    for (var i = 0; i < heads.length; i++) {
      var el = heads[i];

      /* Next-step line FIRST, and independent of the UID strip. AuctionPlus
         gives no slot for one, and the win headline alone does not tell anyone
         what to do next. The CTA is already labelled CLAIM IT (AP translations),
         so this points at it.
         Order matters: this used to sit after the strip, behind the
         `data-tbb-uid-stripped` guard, so once the strip had run the guard
         short-circuited and the line was never appended. Kept as real DOM text
         (not a CSS ::after) so it is selectable and reaches screen readers. */
      /* Row-level next step. This briefly duplicated the page subheader when
         the sub still read "Claim your win to head to checkout..." — the fix
         was to generalise the SUBHEADER (now "Auction orders are shipped after
         payment is received..."), not to drop this line. Division of labour:
         subheader = page-level policy, stated once. This = what to do on THIS
         row, right now. Keep them saying different things. */
      /* State-aware next-step line (LeMarc 2026-07-16). Pending win/offer
         rows carry the CLAIM IT instruction; once the item is claimed the
         forward action is CONTINUE TO CART, so the line must follow.
         "Claimed" is detected two ways because the views use different
         translation strings: the heading contains "accepted" ("You
         accepted this offer" on /offers, "2nd Chance Accepted" on
         /purchases), OR the row already has a Remove-from-cart button
         (item is in the cart, whatever the heading says). Re-evaluated on
         every pass so an injected line updates when the state flips. */
      var row = el.closest('section');
      var claimed = /accepted/i.test(el.textContent) ||
        (row && /remove from cart/i.test(row.textContent));
      var nextText = claimed
        ? "Click 'CONTINUE TO CART' below to checkout."
        : "Click 'CLAIM IT' below to checkout and make it officially yours.";
      var nextEl = el.querySelector('.tbb-win-next');
      if (!nextEl) {
        nextEl = document.createElement('span');
        nextEl.className = 'tbb-win-next';
        nextEl.textContent = nextText;
        el.appendChild(nextEl);
      } else if (nextEl.textContent !== nextText) {
        nextEl.textContent = nextText;
      }

      if (el.getAttribute('data-tbb-uid-stripped') === '1') continue;

      var dot = el.querySelector('span.mx-1');
      if (!dot) continue;

      // Walk forward from the separator, dropping the UID text node and the
      // separator itself. Only touches text nodes that match the UID shape.
      var removed = false;
      var node = dot.nextSibling;
      while (node) {
        var next = node.nextSibling;
        if (node.nodeType === 3 && UID_RE.test(node.textContent)) {
          node.parentNode.removeChild(node);
          removed = true;
        }
        node = next;
      }
      if (removed) {
        dot.parentNode.removeChild(dot);
        el.setAttribute('data-tbb-uid-stripped', '1');
      }

    }
  }


  /* gateBackLink — REMOVED 2026-07-16. It could never run: this script was
     only ever loaded on /apps/auction-plus/*, never on the collector gate.
     The gate now renders the real tab nav server-side (theme.liquid), so a
     back link is redundant anyway. */


  /* ── Cart actions: one column, not two branches ─────────────────────────
     AP renders the pair in unrelated places:
       Remove from cart  -> the order row's right column (.sm:w-40, 160px)
       Continue to cart  -> its own div.flex BELOW the entire section
     So one clipped inside a 160px column and the other went full-width under
     the row. CSS cannot merge separate DOM branches, so move the primary into
     the same column and let CSS stack them (right column on desktop,
     full-width stack on mobile).
     Primary goes FIRST so the forward action reads above the destructive one. */
  /* mergeCartActions — REMOVED 2026-07-15. Do not reinstate without reading this.

     It moved "Continue to cart" into the order row's action column, next to
     "Remove from cart", so the pair would stack on the right. That was wrong:
     the two buttons are not peers.

       .mt-12.space-y-10          (childCount: 2)
         ├── section     "You won the auction!"   <- the order row
         └── div.flex    "Continue to cart"       <- sibling of the WHOLE section

     "Remove from cart" is per-item (it lives in the row's .sm:w-40 column).
     "Continue to cart" is PAGE-LEVEL: it means "your cart has something in it",
     for any item, from any session. Pairing them made a page-level CTA look
     row-level, so it appeared on rows whose item had never been claimed — and
     it would attach to an arbitrary row as soon as there were two orders.

     It also moved a node owned by AP's Vue app, risking the handler on the one
     button that leads to checkout. With the merge gone that risk is gone too.

     Layout is now CSS-only: the CTA stays where AP put it, centered at the
     bottom of the shell. See "Cart CTA" in tbb-auction-dashboard.css. */


  function boot() {
    stripUid(document);
    // The proxy content can hydrate late; re-run on mutation, then stop.
    var mo = new MutationObserver(function () { stripUid(document); });
    mo.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { mo.disconnect(); }, 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The claim/accept state flip happens long after the 10s observer dies,
     so re-run the pass shortly after any click inside the proxy — cheap,
     and it catches the Vue re-render that swaps the heading. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('.auction-proxy')) return;
    setTimeout(function () { stripUid(document); }, 1200);
    setTimeout(function () { stripUid(document); }, 3500);
  });
})();
